import IndexedDbStore from './IndexedDbStore';

export class IndexedDbPersister {
  constructor(name) {
    this.stores = {};
    this.storeConfigs = undefined;
    this.debug = false;
    this.name = name || 'indexedDbPersister';
    this.version = 1;
    this.db = null;
  }

  setDebug(onOrOff) {
    this.debug = Boolean(onOrOff);
    Object.values(this.stores).forEach((s) => s.setDebug(onOrOff));
  }

  debugMsg(...args) {
    if (this.debug || window.showAtsDebugMessage === true) {
      // eslint-disable-next-line no-console
      console.log(`[${this.name}_${this.version}]`, ...args);
    }
  }

  /**
   * This will delete the indexedDB completely. Only use this for logout.
   */
  kill() {
    window.indexedDB.deleteDatabase(this.db.name);
    this.stores = {};
  }

  closeDb() {
    this.db.close();
  }

  connectToDb() {
    return new Promise((resolve, reject) => {
      const that = this; // Need to preserve the scope of `this`.
      const dbOpen = window.indexedDB.open(this.name, this.version);
      dbOpen.addEventListener('blocked', () => {
        console.warn('Cannot open indexedDB, it blocked by another process. Check if other tabs are open');
      });
      // event handlers
      dbOpen.addEventListener('error', (err) => {
        console.warn('TODO: add graceful degradation for error fallback.');
        console.error(err);
        reject(err);
      });

      dbOpen.addEventListener('success', (event) => {
        console.info('Successfully opened indexedDB');
        that.db = event.target.result;
        resolve(that.db);
      });

      dbOpen.addEventListener('upgradeneeded', (event) => {
        that.db = event.target.result;
        Object.values(that.storeConfigs).forEach((s) => {
          if (that.db.objectStoreNames.contains(s.name)) {
            that.db.deleteObjectStore(s.name);
          }
          that.stores[s.name] = that.db.createObjectStore(s.name, { keyPath: s.keyPath });
        });
      });
    });
  }

  async initDataStore(storeConfigs) {
    this.storeConfigs = storeConfigs;
    const db = await this.connectToDb();
    // New map is already set in constructor. Just need to cycle through storage configs
    Object.values(this.storeConfigs).forEach((storeConfig) => {
      const store = new IndexedDbStore(storeConfig.name, this.version, db);
      this.stores[storeConfig.name] = store;
    });
    return this;
  }
}

let indexedDbPersister;

/**
 * Enforces a singleton pattern that always returns a promise resolving a single
 * instance of an IndexedDbPersister.
 * @param {Object} storeIdentifiers A lookup object of store identifiers.
 * @returns Promise<IndexedDbPersister>
 */
export const getIndexedDbPersister = async (name, storeIdentifiers = {}) => {
  if (indexedDbPersister !== undefined) {
    return indexedDbPersister;
  }
  indexedDbPersister = new IndexedDbPersister(name);

  return indexedDbPersister.initDataStore(storeIdentifiers);
};
